import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Hero from "./components/Hero";
import CategoryNav from "./components/CategoryNav";
import VideoSection from "./components/VideoSection";
import ArticleCard from "@/components/base/ArticleCard";
import { videos, getHotVideos, getNewVideos } from "@/mocks/videos";
import { getLatestArticles } from "@/mocks/articles";

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const hotVideos = getHotVideos();
  const newVideos = getNewVideos();
  const latestArticles = getLatestArticles(4);

  // JSON-LD structured data
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "白云视频",
    url: window.location.origin,
    description: "白云视频官网，提供西瓜视频免费和西瓜视频一样的在线看剧体验，免费看电影电视剧，高清不卡顿的视频平台。",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${window.location.origin}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "白云视频",
    url: window.location.origin,
    description: "白云视频 - 免费高清在线影视平台，提供电影、电视剧、综艺、动漫等海量影视内容在线观看。",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: window.location.origin,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <Hero />

          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            {/* Category Navigation */}
            <div className="py-6">
              <CategoryNav />
            </div>

            {/* Hot Today */}
            <VideoSection
              title="今日热播"
              subtitle="大家都在看的精彩内容"
              videos={hotVideos}
              viewAllLink="/category/movie"
            />

            {/* Latest Update */}
            <VideoSection
              title="最新更新"
              subtitle="刚刚上线的热门影视"
              videos={newVideos}
              viewAllLink="/category/tv"
            />

            {/* All Videos */}
            <VideoSection
              title="为你推荐"
              subtitle="根据热门排行精选推荐"
              videos={videos.slice(0, 6)}
              viewAllLink="/category/movie"
            />
          </div>

          {/* Article Section */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "oklch(var(--foreground-900))" }}>
                    影视资讯
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">最新影视动态和深度影评</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-center mb-8" style={{ fontFamily: "var(--font-heading)", color: "oklch(var(--foreground-900))" }}>
                常见问题
              </h2>
              <div className="space-y-3">
                {[
                  { q: "白云视频真的免费吗？", a: "是的，白云视频所有内容完全免费观看，无需购买会员，无需付费。我们坚持为用户提供高质量的免费观影体验。" },
                  { q: "视频画质怎么样？会不会卡顿？", a: "白云视频采用先进的视频压缩和CDN加速技术，提供高清画质的同时确保播放流畅不卡顿。建议使用Wi-Fi或4G/5G网络获得最佳体验。" },
                  { q: "支持哪些设备观看？", a: "白云视频支持电脑、平板、手机等主流设备观看，无需下载APP，打开浏览器即可直接观看。" },
                  { q: "片源更新快吗？", a: "我们的内容团队会持续更新片源，热门影视剧集在首播后会尽快上线，确保您能第一时间观看到最新内容。" },
                ].map((faq, idx) => (
                  <details key={idx} className="group bg-white border border-neutral-100 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-50 transition-colors">
                      <span className="text-sm font-medium text-foreground-800">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: [
                    { "@type": "Question", name: "白云视频真的免费吗？", acceptedAnswer: { "@type": "Answer", text: "是的，白云视频所有内容完全免费观看，无需购买会员，无需付费。" } },
                    { "@type": "Question", name: "视频画质怎么样？会不会卡顿？", acceptedAnswer: { "@type": "Answer", text: "白云视频采用先进的视频压缩和CDN加速技术，提供高清画质的同时确保播放流畅不卡顿。" } },
                    { "@type": "Question", name: "支持哪些设备观看？", acceptedAnswer: { "@type": "Answer", text: "白云视频支持电脑、平板、手机等主流设备观看，无需下载APP，打开浏览器即可直接观看。" } },
                    { "@type": "Question", name: "片源更新快吗？", acceptedAnswer: { "@type": "Answer", text: "热门影视剧集在首播后会尽快上线，确保您能第一时间观看到最新内容。" } },
                  ],
                }),
              }}
            />
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}